import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import EntityMember from "_shared/entity-members/entity-member";
import Individual from "marketplace/individuals/individual";
import MarketplaceTeam from "marketplace/teams/team";
import Marketplace from "work/work-agreements/teams/vendor-team/_views/marketplace/vendor-marketplace";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

/** One firm chosen from the marketplace: its representative, and the name the dialog shows for it. */
export type ChosenFirm = {
  member: EntityMember;
  name: string;
  /**
   * The marketplace team the firm was chosen through, as a draft's vendor team tab builds it: its
   * leader, its members and its marketplace id. Absent for an individual. A proposal sent to
   * several firms is addressed to the whole team (#576).
   */
  team?: VendorTeam;
};

/** Which picker is open: a team leader, or the leader of a marketplace team. */
export enum FirmPickerKind {
  Individual = "individual",
  Team = "team",
}

const PickerContent = styled(DialogContent)(() => ({
  display: "flex",
  height: "75vh",
  minHeight: 0,
  overflow: "hidden",
  padding: 0,
}));
const PickerTitle = styled(DialogTitle)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  paddingRight: theme.spacing(1),
}));

export function firmFromIndividual(individual: Individual): ChosenFirm {
  return {
    member: new EntityMember({ userId: individual.userId, legalEntityId: individual.legalEntityId }),
    name: individual.fullName,
  };
}

/** A team is addressed through its leader, who answers for the team. */
export function firmFromTeam(team: MarketplaceTeam): ChosenFirm {
  return {
    member: new EntityMember({ userId: team.leader.userId, legalEntityId: team.leader.legalEntityId }),
    name: `${team.leader.fullName} (${team.profile.name})`,
    team: VendorTeam.FromMarketplaceTeam(team),
  };
}

/**
 * The marketplace picker the vendor team editor uses, in a dialog over the send-to-several-firms
 * dialog.
 *
 * The individual picker lists vendor representatives and refuses a person without signing
 * authority ("Select As Leader" is disabled): the server will not send a proposal to a firm whose
 * representative cannot sign. The team picker hands back the team, and the team leader is the vendor.
 */
export default function ProposalFirmPicker(
  props: Readonly<{
    kind?: FirmPickerKind;
    onClose: () => void;
    onFirmChosen: (firm: ChosenFirm) => void;
  }>,
) {
  const { kind, onClose, onFirmChosen } = props;
  const title = kind === FirmPickerKind.Team ? "Add a marketplace team" : "Add a team leader";

  return (
    <Dialog open={kind !== undefined} onClose={onClose} fullWidth maxWidth="lg" aria-labelledby="firm-picker-title">
      <PickerTitle id="firm-picker-title">
        {title}
        <IconButton aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </PickerTitle>
      <PickerContent>
        {kind === FirmPickerKind.Team && (
          <Marketplace selectingTeam onTeamSelected={(team) => onFirmChosen(firmFromTeam(team))} />
        )}
        {kind === FirmPickerKind.Individual && (
          <Marketplace
            selectingLeader
            onTeamMemberSelected={(individual) => onFirmChosen(firmFromIndividual(individual))}
          />
        )}
      </PickerContent>
    </Dialog>
  );
}
